import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { ExerciseMedia } from '@/components/ExerciseMedia';
import { MediaUploader } from '@/components/MediaUploader';
import { VideoLibrary } from '@/components/VideoLibrary';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, GripVertical, Download, Film, Sparkles, Repeat, AlertTriangle } from 'lucide-react';
import TrainingPrograms from './TrainingPrograms';
import { TemplateVersionHistory } from '@/components/TemplateVersionHistory';

const CATEGORIES = ['Chest', 'Back', 'Legs', 'Shoulders', 'Arms', 'Core', 'Cardio', 'Walking', 'HIIT', 'Running', 'Fitness Racing', 'Mobility / Recovery'];
const WORKOUT_TYPES = ['Strength Training', 'Fitness Racing', 'Cardio', 'Walking', 'Running', 'HIIT', 'Circuit Training'];

export default function AdminTraining() {
  return (
    <div className="space-y-6" data-testid="admin-training">
      <div>
        <div className="label-mini text-[#00C8FF]">Library & Builder</div>
        <h1 className="font-display font-black text-4xl mt-1">Training</h1>
      </div>

      <Tabs defaultValue="library">
        <TabsList className="bg-regen-card border border-[var(--c-hair)]">
          <TabsTrigger value="library" data-testid="tr-tab-library">Exercise Library</TabsTrigger>
          <TabsTrigger value="builder" data-testid="tr-tab-builder">Workout Builder</TabsTrigger>
          <TabsTrigger value="videos" data-testid="tr-tab-videos">Video Library</TabsTrigger>
          <TabsTrigger value="programs" data-testid="tr-tab-programs">Programs</TabsTrigger>
        </TabsList>
        <TabsContent value="library" className="mt-4"><ExerciseLibrary /></TabsContent>
        <TabsContent value="builder" className="mt-4"><WorkoutBuilder /></TabsContent>
        <TabsContent value="videos" className="mt-4"><VideoLibrary /></TabsContent>
        <TabsContent value="programs" className="mt-4"><TrainingPrograms /></TabsContent>
      </Tabs>
    </div>
  );
}

function ExerciseLibrary() {
  const [exercises, setExercises] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [libOpen, setLibOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'Chest', muscle_group: '', description: '', gif_url: '', coach_notes: '', approved_alternatives: [], status: 'active' });
  const [filter, setFilter] = useState('all');
  const [altSearch, setAltSearch] = useState('');

  const load = async () => { const r = await api.get('/exercises'); setExercises(r.data); };
  useEffect(() => { load(); }, []);

  const open_modal = (ex) => {
    if (ex) { setEditing(ex.id); setForm({ approved_alternatives: [], ...ex }); } else { setEditing(null); setForm({ name: '', category: 'Chest', muscle_group: '', description: '', gif_url: '', coach_notes: '', approved_alternatives: [], status: 'active' }); }
    setAltSearch('');
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.put(`/exercises/${editing}`, form); else await api.post('/exercises', form);
      toast.success('Saved'); setOpen(false); load();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/exercises/${id}`); load(); toast.success('Deleted'); };

  const convertGif = async () => {
    setConverting(true);
    try {
      const r = await api.post('/exercise-media/convert-gif', { url: form.gif_url });
      setForm(f => ({ ...f, gif_url: r.data.url }));
      toast.success('Converted to MP4 & added to your Video Library');
    } catch (err) { toast.error(err.response?.data?.detail || 'Conversion failed'); }
    finally { setConverting(false); }
  };

  const exportVideos = async () => {
    try {
      toast.info('Preparing export…');
      const r = await api.get('/exercise-media/export', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `regen-exercise-videos.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  const filtered = exercises.filter(e => filter === 'all' || e.category === filter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-regen-card border-[var(--c-hair)]" data-testid="ex-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button onClick={exportVideos} data-testid="export-videos-btn" variant="outline" className="border-[var(--c-hair)]"><Download className="w-4 h-4 mr-2" /> Export Videos</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={() => open_modal(null)} data-testid="add-exercise-btn" className="bg-[#00C8FF] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> Add Exercise</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Exercise</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="ex-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                    <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="ex-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Muscle Group</Label><Input value={form.muscle_group} onChange={e => setForm({...form, muscle_group: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              </div>
              <div>
                <Label>Training environment</Label>
                <Select value={form.training_environment || 'both'} onValueChange={v => setForm({...form, training_environment: v})}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="ex-environment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Home & Gym</SelectItem>
                    <SelectItem value="home">Home</SelectItem>
                    <SelectItem value="gym">Gym only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Gym clients can use every exercise. Home clients only get Home or Home & Gym exercises.{form.environment_auto ? ' (Auto-tagged from the name — save to confirm.)' : ''}</p>
              </div>
              <div>
                <Label>Exercise Demo (video or image)</Label>
                <MediaUploader onUploaded={(url) => setForm(f => ({ ...f, gif_url: url }))} label="Upload exercise demo" />
                <Dialog open={libOpen} onOpenChange={setLibOpen}>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline" data-testid="choose-from-library-btn" className="w-full mt-2 border-[var(--c-hair)]"><Film className="w-4 h-4 mr-2" /> Choose from Video Library</Button>
                  </DialogTrigger>
                  <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Reuse a Video</DialogTitle></DialogHeader>
                    <VideoLibrary onPick={(url) => { setForm(f => ({ ...f, gif_url: url })); setLibOpen(false); toast.success('Video selected'); }} />
                  </DialogContent>
                </Dialog>
              </div>
              <div><Label className="text-xs text-muted-foreground">…or paste an external URL (.gif, .mp4, .webm)</Label><Input value={form.gif_url} onChange={e => setForm({...form, gif_url: e.target.value})} data-testid="ex-gif" placeholder="https://… .mp4 or .gif" className="bg-transparent border-[var(--c-hair)]" /></div>
              {/\.gif(\?.*)?$/i.test(form.gif_url || '') && (
                <Button type="button" onClick={convertGif} disabled={converting} data-testid="convert-gif-btn" variant="outline" className="w-full border-[var(--c-green-soft)] text-[var(--c-green-ink)]">
                  <Sparkles className="w-4 h-4 mr-2" /> {converting ? 'Converting…' : 'Convert this GIF → MP4 (smaller, smoother)'}
                </Button>
              )}
              {form.gif_url && <ExerciseMedia src={form.gif_url} alt="preview" className="w-full max-h-48 rounded-lg" fit="contain" />}
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Coach Notes</Label><Textarea value={form.coach_notes} onChange={e => setForm({...form, coach_notes: e.target.value})} className="bg-transparent border-[var(--c-hair)]" /></div>
              <div><Label>Voice coach tempo <span className="text-xs text-muted-foreground font-normal">(e.g. 3-0-1 = 3s lower, 0s pause, 1s lift · "explosive" for swings)</span></Label>
                <Input value={form.tempo || ''} onChange={e => setForm({...form, tempo: e.target.value})} placeholder="3-0-1" data-testid="ex-tempo" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
              <div><Label>Voice coach setup script <span className="text-xs text-muted-foreground font-normal">(spoken before the first set; leave blank to read the description)</span></Label>
                <Textarea value={form.voice_setup || ''} onChange={e => setForm({...form, voice_setup: e.target.value})} placeholder="Grab your dumbbells. Stand with your feet hip-width apart…" data-testid="ex-voice-setup" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div>
                <Label>Approved Alternatives <span className="text-xs text-muted-foreground font-normal">(clients can swap this exercise for any you approve — Strength only)</span></Label>
                {(form.approved_alternatives || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="approved-alt-chips">
                    {(form.approved_alternatives || []).map(aid => {
                      const alt = exercises.find(e => e.id === aid);
                      return (
                        <span key={aid} className="pill bg-[#00C8FF]/10 text-[#00C8FF] text-xs flex items-center gap-1" data-testid={`approved-alt-chip-${aid}`}>
                          {alt?.name || 'Exercise'}
                          <button type="button" onClick={() => setForm(f => ({ ...f, approved_alternatives: (f.approved_alternatives || []).filter(x => x !== aid) }))} className="hover:text-[var(--c-ink)]"><Trash2 className="w-3 h-3" /></button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <Input value={altSearch} onChange={e => setAltSearch(e.target.value)} placeholder="Search exercises to approve…" data-testid="approved-alt-search" className="bg-transparent border-[var(--c-hair)] mt-2" />
                {altSearch.trim() && (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-[var(--c-hair)] divide-y divide-[var(--c-hair)]" data-testid="approved-alt-results">
                    {exercises.filter(e => e.id !== editing && !(form.approved_alternatives || []).includes(e.id) && e.name.toLowerCase().includes(altSearch.toLowerCase())).slice(0, 20).map(e => (
                      <button key={e.id} type="button" data-testid={`approved-alt-add-${e.id}`}
                        onClick={() => { setForm(f => ({ ...f, approved_alternatives: [...(f.approved_alternatives || []), e.id] })); setAltSearch(''); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--c-film)] flex items-center justify-between">
                        <span>{e.name}</span><span className="text-[11px] text-muted-foreground">{e.category}</span>
                      </button>
                    ))}
                    {exercises.filter(e => e.id !== editing && !(form.approved_alternatives || []).includes(e.id) && e.name.toLowerCase().includes(altSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
                    )}
                  </div>
                )}
              </div>
              <Button onClick={save} data-testid="ex-save" className="w-full bg-[#00C8FF] text-[#081726] font-semibold">Save</Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(ex => (
          <div key={ex.id} className="regen-card-elevated overflow-hidden group" data-testid={`exercise-${ex.id}`}>
            {ex.gif_url ? <ExerciseMedia src={ex.gif_url} alt={ex.name} className="w-full aspect-video" fit="contain" /> : <div className="w-full aspect-video bg-[var(--c-film)] flex items-center justify-center text-muted-foreground text-xs">No media</div>}
            <div className="p-4">
              <div className="label-mini">{ex.category} · <span data-testid={`exercise-env-${ex.id}`}>{({ home: 'Home', gym: 'Gym', both: 'Home & Gym' })[ex.training_environment] || 'Home & Gym'}</span></div>
              <div className="font-display font-bold mt-1">{ex.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{ex.muscle_group}</div>
              <div className="flex gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="sm" variant="outline" onClick={() => open_modal(ex)} data-testid={`ex-edit-${ex.id}`} className="border-[var(--c-hair)] h-8"><Edit2 className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(ex.id)} data-testid={`ex-delete-${ex.id}`} className="border-red-500/30 text-red-400 h-8"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkoutBuilder() {
  const [workouts, setWorkouts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [audit, setAudit] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [form, setForm] = useState({ name: '', workout_type: 'Strength Training', workout_format: 'Standard', rounds: 3, rest_between_rounds_seconds: 240, description: '', items: [], is_template: true });

  const load = async () => {
    const w = await api.get('/workouts'); setWorkouts(w.data);
    const e = await api.get('/exercises'); setExercises(e.data);
    try { const a = await api.get('/admin/workouts/audit'); setAudit(a.data); } catch { /* endpoint may be unavailable */ }
  };
  useEffect(() => { load(); }, []);

  const removeAiWorkouts = async () => {
    const n = audit?.ai_generated_workouts || 0;
    if (n <= 0) return;
    if (!confirm(`Remove ${n} AI-generated workout template${n === 1 ? '' : 's'}?\n\nThis permanently deletes ONLY AI-generated templates. Your ${audit?.coach_workouts ?? ''} coach-created circuits (HIIT, Fitness Racing, strength) will NOT be touched.`)) return;
    setCleaning(true);
    try {
      const r = await api.delete('/admin/workouts/ai-generated?confirm=true');
      toast.success(`Removed ${r.data.deleted} AI workout${r.data.deleted === 1 ? '' : 's'} · ${r.data.coach_workouts_after} coach circuits intact`);
      await load();
    } catch { toast.error('Cleanup failed'); }
    finally { setCleaning(false); }
  };

  const open_modal = (w) => {
    if (w) { setEditing(w.id); setForm({ workout_format: 'Standard', rounds: 3, rest_between_rounds_seconds: 240, ...w }); }
    else { setEditing(null); setForm({ name: '', workout_type: 'Strength Training', workout_format: 'Standard', rounds: 3, rest_between_rounds_seconds: 240, description: '', items: [], is_template: true }); }
    setOpen(true);
  };

  const addItem = (ex) => setForm({...form, items: [...form.items, { exercise_id: ex.id, sets: 3, reps: '10', rest_seconds: 60, notes: '' }]});
  const updateItem = (idx, k, v) => { const items = [...form.items]; items[idx] = {...items[idx], [k]: v}; setForm({...form, items}); };
  const removeItem = (idx) => setForm({...form, items: form.items.filter((_, i) => i !== idx)});

  const save = async () => {
    try {
      if (editing) await api.put(`/workouts/${editing}`, form); else await api.post('/workouts', form);
      toast.success('Saved'); setOpen(false); load();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => { if (!confirm('Delete?')) return; await api.delete(`/workouts/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end items-center gap-2">
        <span className="mr-auto text-sm text-muted-foreground" data-testid="workout-audit">
          Coach circuits: <span className="font-semibold text-[var(--c-ink)]" data-testid="coach-workout-count">{audit ? audit.coach_workouts : workouts.length}</span>
        </span>
        <Button
          onClick={removeAiWorkouts}
          disabled={cleaning || !audit || (audit?.ai_generated_workouts || 0) === 0}
          data-testid="remove-ai-workouts-btn"
          variant="outline"
          className="border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
          title={audit && audit.ai_generated_workouts === 0 ? 'No AI-generated workouts to remove' : 'Permanently delete AI-generated workout templates (coach circuits untouched)'}
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {cleaning ? 'Removing…' : `Remove AI-Generated Workouts (${audit ? audit.ai_generated_workouts : 0})`}
        </Button>
        <MigrateHiitDialog onDone={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => open_modal(null)} data-testid="add-workout-btn" className="bg-[#00C8FF] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-2" /> New Workout</Button></DialogTrigger>
          <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Workout Template</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="wo-name" className="bg-transparent border-[var(--c-hair)]" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.workout_type} onValueChange={v => setForm({...form, workout_type: v})}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="wo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{WORKOUT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="wo-description" placeholder="Instructions the client sees before starting (e.g. Warm up 5 min on elliptical. 3 sets of the 7 exercises. 45s recovery between exercises, 4 min between sets.)" className="bg-transparent border-[var(--c-hair)]" /></div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Workout Format</Label>
                <Select value={form.workout_format || 'Standard'} onValueChange={v => setForm({...form, workout_format: v})}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="wo-format"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Standard">Standard (Sets)</SelectItem><SelectItem value="Circuit">Circuit (Rounds)</SelectItem></SelectContent>
                </Select>
              </div>
              {form.workout_format === 'Circuit' && <>
                <div><Label>Number of Rounds</Label><Input type="number" min="1" value={form.rounds} onChange={e => setForm({...form, rounds: parseInt(e.target.value) || 1})} data-testid="wo-rounds" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
                <div><Label>Rest Between Rounds (sec)</Label><Input type="number" min="0" value={form.rest_between_rounds_seconds} onChange={e => setForm({...form, rest_between_rounds_seconds: parseInt(e.target.value) || 0})} data-testid="wo-rest-rounds" className="bg-transparent border-[var(--c-hair)] font-mono" /></div>
              </>}
            </div>
            {form.workout_format === 'Circuit' && (
              <div className="text-[11px] text-muted-foreground">The exercise list below = ONE round; it repeats {form.rounds || 1}×. Each exercise's number is its per-exercise <b>Recovery (sec)</b>; the final exercise of each round uses Rest Between Rounds.</div>
            )}

            {form.workout_type === 'HIIT' && (
              <div>
                <Label>HIIT Level</Label>
                <Select value={form.hiit_level ? String(form.hiit_level) : ''} onValueChange={v => setForm({...form, hiit_level: parseInt(v)})}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="wo-hiit-level"><SelectValue placeholder="Assign a level…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Level 1 — Foundation</SelectItem>
                    <SelectItem value="2">Level 2 — Performance</SelectItem>
                    <SelectItem value="3">Level 3 — Advanced</SelectItem>
                    <SelectItem value="4">Level 4 — Elite</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground mt-1">The AI selects HIIT circuits for a client based on this level. The client can change their own level anytime.</div>
              </div>
            )}

            {form.workout_type === 'Fitness Racing' && (
              <div>
                <Label>Fitness Racing Level</Label>
                <Select value={form.fitness_racing_level ? String(form.fitness_racing_level) : ''} onValueChange={v => setForm({...form, fitness_racing_level: parseInt(v)})}>
                  <SelectTrigger className="bg-transparent border-[var(--c-hair)]" data-testid="wo-fr-level"><SelectValue placeholder="Assign a level…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Level 1 — Foundation</SelectItem>
                    <SelectItem value="2">Level 2 — Performance</SelectItem>
                    <SelectItem value="3">Level 3 — Advanced</SelectItem>
                    <SelectItem value="4">Level 4 — Elite</SelectItem>
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-muted-foreground mt-1">The AI selects Fitness Racing circuits for a client based on this level. The client can change their own level anytime.</div>
                <Label className="mt-3 block">Station transition (seconds)</Label>
                <Input type="number" min="0" value={form.transition_seconds ?? ''} placeholder="e.g. 20"
                  onChange={e => setForm({ ...form, transition_seconds: e.target.value === '' ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                  data-testid="wo-fr-transition" className="bg-transparent border-[var(--c-hair)] font-mono" />
                <div className="text-[11px] text-muted-foreground mt-1">Time to move between stations in the voice-guided race. Leave blank to use each station's rest (or 20s if a station has none).</div>
              </div>
            )}

            <div>
              <Label>Add Exercises</Label>
              <div className="grid grid-cols-4 gap-2 mt-2 max-h-32 overflow-y-auto">
                {exercises.map(ex => <button key={ex.id} onClick={() => addItem(ex)} data-testid={`add-ex-${ex.id}`} className="text-xs p-2 bg-regen-card hover:bg-[var(--c-film)] rounded-lg text-left border border-[var(--c-hair)]">{ex.name}</button>)}
              </div>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {form.items.length > 0 && (
                <div className="flex items-center gap-2 px-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <span className="w-4" /><span className="flex-1">Exercise</span>
                  <span className="w-16 text-center">{form.workout_format === 'Circuit' ? 'Sets' : 'Sets'}</span>
                  <span className="w-4" /><span className="w-16 text-center">Reps</span>
                  <span className="w-20 text-center">Recov(s)</span><span className="w-8" />
                </div>
              )}
              {form.items.map((item, idx) => {
                const ex = exercises.find(e => e.id === item.exercise_id);
                return (
                  <div key={idx} className="p-2 border border-[var(--c-hair)] rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1 text-sm">{ex?.name}</div>
                    <Input type="number" value={item.sets} onChange={e => updateItem(idx, 'sets', parseInt(e.target.value))} className="w-16 bg-transparent border-[var(--c-hair)] text-center font-mono" />
                    <span className="text-muted-foreground">×</span>
                    <Input value={item.reps} onChange={e => updateItem(idx, 'reps', e.target.value)} className="w-16 bg-transparent border-[var(--c-hair)] text-center font-mono" />
                    <Input type="number" value={item.rest_seconds} onChange={e => updateItem(idx, 'rest_seconds', parseInt(e.target.value))} className="w-20 bg-transparent border-[var(--c-hair)] text-center font-mono" />
                    <Button size="sm" variant="ghost" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                  </div>
                  <div className="flex items-center gap-2 pl-6">
                    <Input value={item.tempo || ''} onChange={e => updateItem(idx, 'tempo', e.target.value)} placeholder="Tempo 3-0-1" data-testid={`wo-tempo-${idx}`} className="h-8 flex-1 bg-transparent border-[var(--c-hair)] font-mono text-xs" />
                    <Input value={item.rep_scheme || ''} onChange={e => updateItem(idx, 'rep_scheme', e.target.value)} placeholder="Weekly reps 8,10,8,10" data-testid={`wo-rep-scheme-${idx}`} className="h-8 flex-1 bg-transparent border-[var(--c-hair)] font-mono text-xs" />
                  </div>
                  </div>
                );
              })}
            </div>
            <Button onClick={save} data-testid="wo-save" className="w-full bg-[#00C8FF] text-[#081726] font-semibold">Save Template</Button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {workouts.map(w => (
          <div key={w.id} className="regen-card-elevated p-5" data-testid={`workout-${w.id}`}>
            <div className="label-mini">{w.workout_type}{w.workout_format === 'Circuit' ? ' · Circuit' : ''}{w.workout_type === 'HIIT' && w.hiit_level ? ` · L${w.hiit_level}` : ''}{w.workout_type === 'Fitness Racing' && w.fitness_racing_level ? ` · L${w.fitness_racing_level}` : ''}</div>
            <div className="font-display font-bold text-lg mt-1">{w.name}</div>
            <div className="text-xs text-muted-foreground mt-1">{w.items?.length || 0} exercises{w.workout_format === 'Circuit' ? ` · ${w.rounds || 1} rounds` : ''}</div>
            <div className="flex gap-2 mt-4 items-center">
              <Button size="sm" variant="outline" onClick={() => open_modal(w)} data-testid={`wo-edit-${w.id}`} className="border-[var(--c-hair)]"><Edit2 className="w-3 h-3 mr-1.5" /> Edit</Button>
              <Button size="sm" variant="outline" onClick={() => del(w.id)} data-testid={`wo-delete-${w.id}`} className="border-red-500/30 text-red-400"><Trash2 className="w-3 h-3" /></Button>
              <div className="ml-auto"><TemplateVersionHistory ttype="workouts" tid={w.id} name={w.name} onRestored={load} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// Admin tool: convert legacy HIIT/Circuit templates (rounds only in Description) to structured data.
function MigrateHiitDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const r = await api.get('/workouts/migrate-hiit/scan');
      setRows((r.data || []).map(w => ({ ...w, rounds: w.suggested_rounds ?? '', rest: 240, saving: false, done: false })));
    } catch { toast.error('Scan failed'); }
    setLoading(false);
  };
  useEffect(() => { if (open) scan(); }, [open]);

  const setField = (id, k, v) => setRows(rs => rs.map(x => x.id === id ? { ...x, [k]: v } : x));

  const convert = async (row) => {
    const n = parseInt(row.rounds);
    if (!n || n < 1) { toast.error('Enter a valid round count first'); return; }
    setField(row.id, 'saving', true);
    try {
      await api.post(`/workouts/${row.id}/convert-circuit`, { rounds: n, rest_between_rounds_seconds: parseInt(row.rest) || 240 });
      setField(row.id, 'done', true);
      toast.success(`${row.name} → Circuit (${n} rounds)`);
      onDone?.();
    } catch { toast.error('Convert failed'); setField(row.id, 'saving', false); }
  };

  const pending = rows.filter(r => !r.done);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="migrate-hiit-btn" className="border-[#FFB800]/40 text-[#FFB800]"><Repeat className="w-4 h-4 mr-2" /> Migrate HIIT → Circuit</Button>
      </DialogTrigger>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="migrate-hiit-dialog">
        <DialogHeader><DialogTitle>Migrate HIIT / Circuit templates</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Legacy HIIT templates whose round count lived only in the Description. A round count is <b>suggested only when clearly stated</b> — otherwise it's flagged for you to enter manually. Nothing is guessed.</p>
        {loading && <div className="text-sm text-muted-foreground py-6 text-center">Scanning…</div>}
        {!loading && pending.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center" data-testid="migrate-empty">🎉 No HIIT templates need migration.</div>}
        <div className="space-y-3">
          {pending.map(row => (
            <div key={row.id} className="regen-card p-3" data-testid={`migrate-row-${row.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{row.name}</div>
                  <div className="text-[11px] text-muted-foreground">{row.workout_type} · {row.exercise_count} exercises</div>
                </div>
                {row.suggested_rounds == null && (
                  <span className="pill bg-[#FFB800]/10 text-[#FFB800] shrink-0 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" /> Needs review</span>
                )}
              </div>
              {row.description && <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2 italic">“{row.description}”</div>}
              <div className="flex items-end gap-2 mt-2">
                <div className="w-24">
                  <Label className="text-[10px]">Rounds</Label>
                  <Input type="number" min="1" value={row.rounds} placeholder="?" onChange={e => setField(row.id, 'rounds', e.target.value)} data-testid={`migrate-rounds-${row.id}`} className="bg-transparent border-[var(--c-hair)] font-mono h-9" />
                </div>
                <div className="w-32">
                  <Label className="text-[10px]">Rest/rounds (s)</Label>
                  <Input type="number" min="0" value={row.rest} onChange={e => setField(row.id, 'rest', e.target.value)} data-testid={`migrate-rest-${row.id}`} className="bg-transparent border-[var(--c-hair)] font-mono h-9" />
                </div>
                <Button size="sm" onClick={() => convert(row)} disabled={row.saving} data-testid={`migrate-convert-${row.id}`} className="bg-[#00C8FF] text-[#081726] font-semibold h-9">
                  {row.saving ? 'Saving…' : 'Convert'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
